import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LoginHeroPanel } from '@/modules/auth/components/login-hero-panel';
import { LoginBrandLogo } from '@/modules/auth/components/login-brand-logo';
import { LOGIN_COLORS } from '@/modules/auth/constants/login-theme';
import { useAuthState } from '@/state/client-middleware';
import { useTheme } from '@/styles/theme/theme-provider';
import { LanguageSelector, ThemeSwitcher } from '@/components/core';

const bgAuthLight = '/assets/images/bg_auth_light.svg';
const bgAuthDark = '/assets/images/bg_auth_dark.svg';

const HERO_AUTH_ROUTES = ['/login', '/activate', '/activate-failed', '/success'];
// IAM's activation link with OIDC on: `/oidc/activate/<tenantId>`.
const OIDC_ACTIVATE_PREFIX = '/oidc/activate/';

export const AuthLayout = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMounted, isAuthenticated } = useAuthState();
  const { theme } = useTheme();
  const isHeroAuthPage =
    HERO_AUTH_ROUTES.includes(location.pathname) ||
    location.pathname.startsWith(OIDC_ACTIVATE_PREFIX);
  const [langPortalContainer, setLangPortalContainer] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  if (!isMounted) return null;

  const getBackgroundImage = () => {
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? bgAuthDark : bgAuthLight;
    }
    return theme === 'dark' ? bgAuthDark : bgAuthLight;
  };

  if (isHeroAuthPage) {
    return (
      <div
        // The sign-in pages are always light: `light` re-applies the light tokens under a dark <html>.
        className="light min-h-dvh w-full"
        style={{ backgroundColor: LOGIN_COLORS.pageBg }}
      >
        <div
          ref={setLangPortalContainer}
          className="fixed right-4 top-4 z-nav sm:right-6 sm:top-6"
        >
          <LanguageSelector portalContainer={langPortalContainer} />
        </div>

        <div className="flex min-h-dvh w-full items-stretch gap-6 px-0 py-3 sm:pl-3 sm:pr-3 lg:pr-6">
          <div
            className="hidden min-w-0 flex-[0_0_48.33%] overflow-hidden rounded-[24px] lg:block"
            style={{ boxShadow: LOGIN_COLORS.cardShadow }}
          >
            <LoginHeroPanel />
          </div>

          <div className="relative flex min-w-0 flex-1 flex-col items-center overflow-y-auto px-6 py-10 sm:px-10 sm:py-6">
            <div className="flex w-full flex-1 flex-col items-center justify-center">
              <div className="flex w-full max-w-[26rem] flex-col items-center gap-10 px-1 py-4">
                <LoginBrandLogo className="h-12" />
                <div className="w-full">
                  <Outlet />
                </div>
              </div>
            </div>

            <p className="mb-2 pt-6 text-center text-xs" style={{ color: LOGIN_COLORS.textMuted }}>
              {t('FOOTER_COPYRIGHT', { year: new Date().getFullYear() })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col h-dvh">
      <div className="flex w-full min-h-dvh relative">
        <div className="hidden md:block w-[36%] relative bg-accent">
          <img
            src={getBackgroundImage()}
            alt="bg auth"
            className="w-full h-full object-cover"
            key={theme ?? 'default'}
          />
        </div>
        <div className="flex items-center justify-center w-full px-6 sm:px-20 md:w-[64%] md:px-[14%] lg:px-[16%] 2xl:px-[20%]">
          <div className="absolute top-2 right-4">
            <div className="flex flex-row gap-2">
              <ThemeSwitcher />
              <LanguageSelector />
            </div>
          </div>
          <Outlet />
        </div>
      </div>
    </div>
  );
};
